import { Component, EventEmitter, HostListener, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { RequestModel } from 'src/app/models/requests';
import { DocumentApiClient } from 'src/app/services/document.apiclient';
import { PagesApiClient } from 'src/app/services/api-clients/pages.api-client';
import { PerformActionDocumentPdfImageComponent } from '../performactiondocumentpdfimage/performactiondocumentpdfimage.component';
import { HttpErrorResponse } from '@angular/common/http';
import { DocumentActor } from 'src/app/models/actors';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { DocumentModel, PageSize, PreloadedPages } from 'src/app/models/document.model';
import { LoggingService } from 'src/app/services/logging.service';
import { Is_Sign_Action } from 'src/app/constants/actor-action.enum';

@Component({
  selector: 'app-performactiondocumentpdf',
  templateUrl: './performactiondocumentpdf.component.html'
})

export class PerformActionDocumentPdfComponent implements OnInit {
  @HostListener('window:scroll') async onScroll() {
    this.getActivepage();
    for (let i = this.activePage - 3; i <= this.activePage + 3; i++) {
      this.loadPage(this.document, i);
    }
  }
  @Input('document') document!: DocumentModel;
  @Input('request') request!: RequestModel;
  @Input('buttonsEnabled') buttonsEnabled!: BehaviorSubject<boolean>;
  @Output('sign') signEmitter = new EventEmitter;
  @Input('actions') actions!: DocumentActor[];
  @Input('actionsOtherActors') actionsOtherActors!: DocumentActor[];
  @Input() zoomFactor!: number;
  defaultPreloadPages: number = 5;
  activePage: number = 0;
  pageId!: number;
  updatingDocument: boolean = false;
  pages!: number;
  @ViewChildren('performActionDocumentPdfImage')
  public performActionDocumentPdfImageComponent!: QueryList<PerformActionDocumentPdfImageComponent>;
  pageSizes: PageSize[] = [];
  preloadedPagesByDocument: PreloadedPages[] = []; 

  constructor(private sanitizer: DomSanitizer, private pageService: PagesApiClient, private documentService: DocumentApiClient,
    private loggingService: LoggingService) {
    //function cannot be empty
  }


  async ngOnInit(): Promise<void> {
    await this.GetDocument();
  }

  private async GetDocument() {
    (await this.documentService.getDocumentForViewer(this.request.id, this.document.id))
      .subscribe({
        error: (error: HttpErrorResponse) => console.error(error),
        next: (value: any) => {
          this.pages = value?.pages;
          this.document.pages = value?.pages;
          this.document.pageSizes = value?.pageSizes;
          this.pageSizes = [];
          JSON.parse(this.document.pageSizes).forEach((pageSize: PageSize) => {
            this.pageSizes.push({
              X: pageSize.X,
              Y: pageSize.Y
            });
          });
          if (this.preloadedPagesByDocument === undefined) this.preloadedPagesByDocument = [];
          let letPagesToLoad: number = this.document.pages < 5 ? this.document.pages : 5;
          for (let i = 1; i <= letPagesToLoad; i++) {
            this.loadPage(this.document, i);
          }
          this.actions.forEach(s => {
            let startPage = s.sigFieldPage - 2 < 0 ? 0 : s.sigFieldPage - 2;
            let lastpage = s.sigFieldPage + 2 > this.document.pages ? this.document.pages : s.sigFieldPage + 2;
            for (let i = startPage; i <= lastpage; i++) {
              this.loadPage(this.document, i);
            }
          })
        }
      });
  }

  getActiveAction() {
    return Math.min(...this.actions.filter(p => p.hasSigned == false).map(o => o.order))
  }

  public getActionsByPage(page: number) {
    let documentActors: any[] = this.actions;
    return documentActors.filter(p => p.sigFieldPage == page && Is_Sign_Action(p.action) && !p.hasSigned && this.document.actors.some((d: { id: any; }) => d.id == p.id)).sort((a,b) => (a.order > b.order ? -1 : 1))
  }

  public getActionsOtherActorsByPage(page: number) {
    let otherDocumentActors: DocumentActor[] = this.actionsOtherActors;
    return otherDocumentActors.filter(p => p.sigFieldPage == page && Is_Sign_Action(p.action));
  }

  loadPage(doc: DocumentModel, pageId: number) {
    if (pageId < 1) return;
    if (pageId <= doc.pages && this.preloadedPagesByDocument.findIndex((s: PreloadedPages) => s.PageId === pageId) === -1) {
      let preloadedPage: PreloadedPages = {
        PageId: pageId,
        PageUrl: null
      }
      this.preloadedPagesByDocument.push(preloadedPage);
      this.pageService.getPage(this.request.id, doc.id, pageId)
        .then(value => {
          lastValueFrom(value)
            .then(page => {
              let pageImage = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(page));
              let preloadPageIndex = this.preloadedPagesByDocument.findIndex(s => s.PageId === pageId);
              this.preloadedPagesByDocument[preloadPageIndex].PageUrl = pageImage;
            })
            .catch((error) => this.loggingService.logException(error));
        })
        .catch((error) => this.loggingService.logException(error));
    }
  }

  public SignAction(event: any) {
    this.signEmitter.emit(event);
  }


  public async reloadSignaturePage(pageId: number) {
    try {
      let getPage = await this.pageService.getPage(this.request.id, this.document.id, pageId);
      let data = await lastValueFrom(getPage);

      let preloadPageIndex = this.preloadedPagesByDocument.findIndex(s => s.PageId === pageId);
      let pageUrl = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(data));
      this.preloadedPagesByDocument[preloadPageIndex].PageUrl = pageUrl;

      if (!this.updatingDocument) {
        this.updatingDocument = false;
      }
    } catch {
      this.updatingDocument = false;
    }
  }

  private getActivepage() {
    for (let i: number = 1; i <= this.pageSizes.length; i++) {
      let pageElement: HTMLElement = <HTMLElement>document.getElementById(this.document.id + "_" + i + "_indicator");
      if (this.elementInViewport(pageElement)) {
        this.activePage = i;
        for (let j = this.activePage - 3; j <= this.activePage + 3; j++) {
          this.loadPage(this.document, j);
        }
        break;
      }
    }
  }

  private elementInViewport(pageElement: HTMLElement) {
    let bounding = pageElement.getBoundingClientRect();
    let x = (
      bounding.bottom < 135 + 0.3 * (window.innerHeight || document.documentElement.clientHeight) ||
      bounding.top > (window.innerHeight || document.documentElement.clientHeight)
    );
    return !x;
  }

  checkIfItemIsRendered(pageId: number) {
    if ( this.preloadedPagesByDocument.findIndex((s: PreloadedPages) => s.PageId === pageId) > -1 &&  this.preloadedPagesByDocument.find((s: PreloadedPages) => s.PageId === pageId)?.PageUrl !== null) return true;
    return false;
  }

  getPageUrl(pageId: number) {
    return  this.preloadedPagesByDocument.find((s: PreloadedPages) => s.PageId === pageId)?.PageUrl;
  }
}

